import React, {PropsWithChildren, useEffect, useRef} from 'react';
import {useDispatch} from 'react-redux';
import {scrollLeftAction, scrollRightAction} from '../../store/actions/presentation.actions';
import {JS_INTERACTABLE_ELEMENT} from '../../constants';

export const JS_HORIZONTAL_SCROLLABLE_ELEMENT = 'js-horizontal-scrollable-element';

const SWIPE_MIN_DISTANCE = 100;
const SWIPE_HORIZONTAL_RATIO = 1.5;

function SlideSwiper({children}: PropsWithChildren<unknown>): JSX.Element {
    const containerRef = useRef<HTMLDivElement>(null);
    const dispatch = useDispatch();

    useEffect(() => {
        const container = containerRef.current;
        if (!container) return undefined;

        let start: {x: number; y: number; id: number} | null = null;
        let ignoreGesture = false;

        const reset = (): void => {
            start = null;
            ignoreGesture = false;
        };

        const handleTouchStart = (e: TouchEvent): void => {
            if (e.touches.length !== 1) {
                reset();
                return;
            }

            const touch = e.touches[0];
            const target = e.target as Element;
            ignoreGesture = !!target.closest(`.${JS_INTERACTABLE_ELEMENT}, .${JS_HORIZONTAL_SCROLLABLE_ELEMENT}`);
            start = {x: touch.clientX, y: touch.clientY, id: touch.identifier};
        };

        const handleTouchEnd = (e: TouchEvent): void => {
            const gestureStart = start;
            const ended = gestureStart ? Array.from(e.changedTouches).find((touch) => touch.identifier === gestureStart.id) : undefined;

            if (!ended || !gestureStart || ignoreGesture) {
                if (e.touches.length === 0) reset();
                return;
            }

            const deltaX = ended.clientX - gestureStart.x;
            const deltaY = ended.clientY - gestureStart.y;
            reset();

            if (Math.abs(deltaX) < SWIPE_MIN_DISTANCE || Math.abs(deltaX) < SWIPE_HORIZONTAL_RATIO * Math.abs(deltaY)) {
                return;
            }

            dispatch(deltaX > 0 ? scrollLeftAction() : scrollRightAction());
        };

        container.addEventListener('touchstart', handleTouchStart, {passive: true});
        container.addEventListener('touchend', handleTouchEnd, {passive: true});
        container.addEventListener('touchcancel', reset, {passive: true});

        return () => {
            container.removeEventListener('touchstart', handleTouchStart);
            container.removeEventListener('touchend', handleTouchEnd);
            container.removeEventListener('touchcancel', reset);
        };
    }, [dispatch]);

    return <div ref={containerRef}>{children}</div>;
}

export default SlideSwiper;
